import { api } from '@/util/api'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import type {
  EventHosts,
  HostInvitation,
  HostsApiErrorType,
  Manager,
  ManagerInvitation,
  ManagerInvitationBinding,
  ManagerInvitee,
  ManagerInviteTarget,
  StageOwner,
} from './types'

const path = encodeURIComponent

/**
 * The organizer's host endpoints (docs/plans/2026-10-19-watch-invites.md, "API contract"): anyone who may update the
 * event lists, invites and removes its hosts. The shared client adds the session token and resolves to the response
 * body; 204 answers resolve to nothing. `list` resolves to the raw body: read it with `readEventHosts`.
 */
export const hostsApi = {
  list: (eventId: string) => api.get<unknown>(`/events/${path(eventId)}/hosts`),
  invite: (eventId: string) => api.post<{ data: HostInvitation }>(`/events/${path(eventId)}/host_invitations`),
  revokeInvitation: (eventId: string, invitationId: string) =>
    api.delete<void>(`/events/${path(eventId)}/host_invitations/${path(invitationId)}`),
  /** Removes the host's stage: their live connections are lowered and the stage's chat is deleted. */
  removeHost: (eventId: string, stageId: string) => api.delete<void>(`/events/${path(eventId)}/hosts/${path(stageId)}`),
  /** Invites an address, or an account by its username, to manage the event: the API emails the link (to an account's
   *  own address, which it never shows), and answers with it once for copying. */
  inviteManager: (eventId: string, target: ManagerInviteTarget) =>
    api.post<{ data: unknown }>(`/events/${path(eventId)}/manager_invitations`, target),
  /** A single-use manager link for 48 hours, bound to nobody: whoever opens it first becomes a manager. Answers with
   *  the link once. */
  createManagerLink: (eventId: string) =>
    api.post<{ data: unknown }>(`/events/${path(eventId)}/manager_invitations`, { link: true }),
  revokeManagerInvitation: (eventId: string, invitationId: string) =>
    api.delete<void>(`/events/${path(eventId)}/manager_invitations/${path(invitationId)}`),
  /** Takes the manager off the event; your own id leaves it. */
  removeManager: (eventId: string, userId: string) =>
    api.delete<void>(`/events/${path(eventId)}/managers/${path(userId)}`),
}

const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null
const text = (value: unknown): value is string => typeof value === 'string'
const textOrNull = (value: unknown) => (text(value) && value !== '' ? value : null)

/** A manager, named by their public profile's name, else "@username", else "Manager": never by an address (the API's
 *  `name` may be null). */
function readManager(value: unknown): Manager | null {
  if (!record(value) || !text(value.user_id)) return null
  const named = (part: unknown): part is string => text(part) && part.trim() !== ''
  return {
    user_id: value.user_id,
    name: named(value.name) ? value.name : named(value.username) ? `@${value.username}` : 'Manager',
    username: textOrNull(value.username),
    avatar_url: textOrNull(value.avatar_url),
    email: textOrNull(value.email),
  }
}

const BINDINGS: readonly ManagerInvitationBinding[] = ['email', 'account', 'link']

function readInvitee(value: unknown): ManagerInvitee | null {
  if (!record(value) || !text(value.user_id)) return null
  const username = textOrNull(value.username)
  return {
    user_id: value.user_id,
    username,
    name: textOrNull(value.name) ?? username ?? 'Someone',
    avatar_url: textOrNull(value.avatar_url),
  }
}

/** A manager invitation from the list or the create answer, or null for one that can't be read. An older API sent no
 *  `binding`: its invitations were all to an address. An account invitation needs its account. */
export function readManagerInvitation(value: unknown): ManagerInvitation | null {
  if (!record(value) || value.kind !== 'manager' || !text(value.id)) return null
  const email = textOrNull(value.email)
  const invitee = readInvitee(value.invitee)
  const binding = BINDINGS.find((option) => option === value.binding) ?? (email ? 'email' : null)
  if (!binding || (binding === 'account' && !invitee)) return null
  return {
    id: value.id,
    kind: 'manager',
    binding,
    email: binding === 'email' ? email : null,
    invitee: binding === 'account' ? invitee : null,
    ...(text(value.url) && value.url ? { url: value.url } : {}),
    created_at: text(value.created_at) ? value.created_at : '',
    expires_at: text(value.expires_at) ? value.expires_at : '',
  }
}

/**
 * What the "Email or @username" field asks for, by its shape: an address has an "@" after its first character;
 * anything else is a username, with or without its leading "@". Null when there's nothing to send.
 */
export function managerInviteTarget(value: string): ManagerInviteTarget | null {
  const entry = value.trim()
  if (/^[^@\s]+@/.test(entry)) return { email: entry }
  const username = entry.replace(/^@+/, '').trim()
  return username ? { username } : null
}

/** How the target reads back to the organizer: the address, or "@username". */
export const targetLabel = (target: ManagerInviteTarget) => ('email' in target ? target.email : `@${target.username}`)

function readOwner(value: unknown): StageOwner | null {
  if (!record(value) || !record(value.profile) || typeof value.path !== 'string') return null
  const { handle, name, avatar_url: avatarUrl } = value.profile
  if (typeof handle !== 'string' || typeof name !== 'string') return null
  return {
    profile: { handle, name, avatar_url: typeof avatarUrl === 'string' ? avatarUrl : null },
    path: value.path,
    live: value.live === true,
  }
}

/** The list answer's `data`, or null when the body isn't one (no lists, or a page in place of JSON). Its invitations
 *  split by kind: manager ones (`kind: "manager"`) apart from host links (any other kind, or none). */
export function readEventHosts(body: unknown): EventHosts | null {
  const data = record(body) ? body.data : null
  if (!record(data) || !Array.isArray(data.hosts) || !Array.isArray(data.invitations)) return null
  const invitations: HostInvitation[] = []
  const managerInvitations: ManagerInvitation[] = []
  for (const item of data.invitations as unknown[]) {
    if (record(item) && item.kind === 'manager') {
      const invitation = readManagerInvitation(item)
      if (invitation) managerInvitations.push(invitation)
    } else invitations.push(item as HostInvitation)
  }
  const managers = Array.isArray(data.managers)
    ? data.managers.flatMap((item: unknown) => readManager(item) ?? [])
    : []
  return { hosts: data.hosts, invitations, managers, managerInvitations, owner: readOwner(data.owner) }
}

/** 422 types whose message is written for people. */
const SPOKEN_422_TYPES: readonly string[] = ['validation_error']

/**
 * What to tell the organizer when a host call fails, by status: a 403 is ours to word; a 409, and a 422 of a known
 * type, say why in the API's message; anything else (401, 404, 5xx, no answer) takes `fallback`.
 */
export function hostsError(error: unknown, fallback: string): string {
  const failure = apiFailure<HostsApiErrorType>(error)
  if (failure.status === 403) return 'Only this event’s organizers can manage its hosts.'
  if (failure.status === 409) return failure.message ?? fallback
  if (failure.status === 422 && failure.type && SPOKEN_422_TYPES.includes(failure.type)) return failure.message ?? fallback
  return fallback
}

/**
 * What to tell the organizer when inviting a manager fails, beside the field where it's about what they entered or
 * when to try again: a 422 is the address (the API's message for a known type, else ours), except
 * `too_many_invitations`; 404 `user_not_found` and 409 `already_manager` name who; a 429 says when (a day's limit, or
 * too many at once); a 403 is ours; anything else as `hostsError`. `target` is left out for a link.
 */
export function managerInviteError(error: unknown, target?: ManagerInviteTarget): { field?: string; notice?: string } {
  const failure = apiFailure<HostsApiErrorType>(error)
  const who = target ? targetLabel(target) : null
  const wait = waitWords(failure.retryAfterMs)
  const problem = (message: string) => (target ? { field: message } : { notice: message })
  if (failure.status === 422 && failure.type === 'too_many_invitations') {
    return { notice: 'Too many invites are open. Revoke one, then try again.' }
  }
  if (failure.status === 422 && target) {
    const spoken = failure.type && SPOKEN_422_TYPES.includes(failure.type) ? failure.message : null
    return { field: spoken ?? 'Enter a valid email address.' }
  }
  if (failure.status === 404 && failure.type === 'user_not_found' && who) return { field: `No one on StreamBridge is ${who}.` }
  if (failure.status === 409 && failure.type === 'already_manager' && who) return { field: `${who} already manages this event.` }
  if (failure.status === 429 && failure.type === 'invitation_daily_limit') {
    return problem(wait ? `No more invites today. Try again in ${wait}.` : 'No more invites today. Try again tomorrow.')
  }
  if (failure.status === 429) {
    return problem(wait ? `Too many invites at once. Try again in ${wait}.` : 'Too many invites at once. Try again shortly.')
  }
  if (failure.status === 403) return { notice: 'Only this event’s organizers can invite managers.' }
  return { notice: hostsError(error, target ? 'The invite couldn’t be sent. Try again.' : 'A manager link couldn’t be made. Try again.') }
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const count = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`

/** "Expires in 14 days", in whole days rounded up, or hours on the last day; "Expired" once past. */
export function expiresIn(expiresAt: string, now = Date.now()): string {
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) return ''
  const left = at - now
  if (left <= 0) return 'Expired'
  if (left >= DAY) return `Expires in ${count(Math.ceil(left / DAY), 'day')}`
  return `Expires in ${count(Math.ceil(left / HOUR), 'hour')}`
}

/** "Made 2 days ago", in whole units rounded down; "Made just now" under a minute. */
export function madeAgo(createdAt: string, now = Date.now()): string {
  const at = Date.parse(createdAt)
  if (Number.isNaN(at)) return ''
  const age = now - at
  if (age >= DAY) return `Made ${count(Math.floor(age / DAY), 'day')} ago`
  if (age >= HOUR) return `Made ${count(Math.floor(age / HOUR), 'hour')} ago`
  if (age >= MINUTE) return `Made ${count(Math.floor(age / MINUTE), 'minute')} ago`
  return 'Made just now'
}
